import { useDashboardSurveyList } from './useDashboardSurveyList';
import { FILTER_STATUS_LIST } from './dashboard-survey-list.constants';
import type { EntityFilter } from './dashboard-survey-list.constants';
import { capitalize, debounce } from 'lodash';
import { Button } from 'src/components/ui/button';
import { Input } from 'src/components/ui/input';
import { Search, Link2 } from 'lucide-react';
import { useState } from 'react';
import { useSearchParams, useParams, useNavigate } from 'react-router-dom';

const DashboardSurveyToolbar = () => {
  const {
    activeStatus,
    activeEntity,
    onFilterSelected,
    onEntitySelected,
    onTitleSearch,
    onUserFilterSelected,
    showAllUsers,
  } = useDashboardSurveyList();
  const [searchParams] = useSearchParams();
  const { clientId, tab } = useParams<{ clientId?: string; tab?: string }>();
  const navigate = useNavigate();
  const isCombineSurveyTab = tab === 'combine';
  const initialSearchValue = searchParams.get('title') || '';
  const [searchValue, setSearchValue] = useState(initialSearchValue);

  const debouncedSearch = debounce((value: string) => {
    onTitleSearch(value);
  }, 300);

  const renderFilterOption = (status: string, isActive: boolean) => {
    return (
      <Button
        size='med'
        className={isActive ? '' : 'bg-gray-201 hover:bg-blue-500 hover:text-white hover:opacity-75'}
        variant={isActive ? 'primary' : 'gray'}
        onClick={() => onFilterSelected(status)}
        key={status}
      >
        {capitalize(status)}
      </Button>
    );
  };

  const renderEntityOption = (entity: EntityFilter, label: string) => {
    const isActive = activeEntity === entity;
    return (
      <Button
        size='med'
        className={isActive ? '' : 'bg-gray-201 hover:bg-blue-500 hover:text-white hover:opacity-75'}
        variant={isActive ? 'primary' : 'gray'}
        onClick={() => onEntitySelected(entity)}
        key={entity}
        type="button"
      >
        {label}
      </Button>
    );
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setSearchValue(newValue);
    debouncedSearch(newValue);
  };

  const goCombine = () => {
    if (clientId) {
      navigate(`/client/${clientId}/surveys/combine`);
    } else {
      navigate('/surveys/combine');
    }
  };

  return (
    <div className="flex flex-col flex-wrap md:flex-row sm:justify-between items-center gap-5">
      {!isCombineSurveyTab && (
        <>
          <div className="flex flex-row flex-wrap gap-2 justify-end">
            {renderEntityOption('all', 'All')}
            {renderEntityOption('surveys', 'Surveys')}
            {renderEntityOption('groups', 'Groups')}
          </div>
          <div className="h-8 border-l-1 border-black/10 -mx-1 hidden md:block" />
        </>
      )}
      <div className="flex flex-row gap-2 justify-end">
        {renderFilterOption('All', activeStatus === undefined)}
        {FILTER_STATUS_LIST.map((status) => renderFilterOption(status, activeStatus === status))}
      </div>
      <div className="h-8 border-l-1 border-black/10 -mx-1 hidden md:block" />
      <div className="flex flex-row gap-2">
        <Button
          size='med'
          variant={!showAllUsers ? 'primary' : 'gray'}
          className={!showAllUsers ? 'bg-green-1000' : 'bg-gray-201 hover:bg-green-1000 hover:text-white hover:opacity-75'}
          onClick={() => onUserFilterSelected(false)}
          type="button"
        >
          Mine
        </Button>
        <Button
          size='med'
          variant={showAllUsers ? 'primary' : 'gray'}
          className={showAllUsers ? 'bg-green-1000' : 'bg-gray-201 hover:bg-green-1000 hover:text-white hover:opacity-75'}
          onClick={() => onUserFilterSelected(true)}
          type="button"
        >
          All
        </Button>
      </div>
      <div className="relative flex-1 md:order-first w-full">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
        <Input
          type="text"
          placeholder="Search"
          value={searchValue}
          onChange={handleSearchChange}
          className="pl-10 bg-white rounded-full placeholder:text-black/75 h-10 border-none shadow-none focus:outline-none focus:ring-0 focus:border-gray-300 focus-visible:outline-none focus:ring-0 focus:ring-transparent"
        />
      </div>
      {!isCombineSurveyTab && (
        <>
          <div className="h-8 border-l-1 border-black/10 -mx-1 hidden lg:block" />
          <Button
            type="button"
            variant="outline"
            size="med"
            className="shrink-0"
            onClick={goCombine}
          >
            <Link2 className="h-4 w-4 mr-1" />
            Combine
          </Button>
        </>
      )}
    </div>
  );
};

export default DashboardSurveyToolbar;
